import { useEffect, useState } from "react";
import api from "../lib/api";
import { setVoicePrefs } from "../lib/voice";

/** Voice preferences modal — mode (push-to-talk/continuous/meeting),
 *  language, TTS feedback, wake word, transcript privacy. */
export default function VoiceSettings({ open, onClose }) {
  // Spec §3: TTS default = OFF on desktop, ON on mobile/tablet (≤1024 px).
  const _isMobileOrTablet = typeof window !== "undefined"
      && window.innerWidth <= 1024;
  const [s, setS] = useState({ mode: "push_to_talk", language: "en",
                                          feedback: _isMobileOrTablet,
                                          wake_word: "Hey Capexara Process Industry",
                                          store_transcripts: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/voice/settings").then(r => { if (r.data) {
      setS(prev => ({ ...prev, ...r.data }));
      setVoicePrefs(r.data);
    } }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/voice/settings", s); setVoicePrefs(s); onClose?.(); }
    catch (_) { /* keep open */ }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <div data-testid="voice-settings" style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        zIndex: 1000, display: "flex", alignItems: "center",
        justifyContent: "center", padding: 32 }}>
      <div style={{ background: "var(--pf-card)", border: "1px solid var(--pf-border)",
                          borderRadius: 8, padding: 24, maxWidth: 480, width: "100%" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#F26B1F",
                            marginBottom: 12 }}>Voice settings</div>
        <Row label="Mode" k="mode" v={s.mode} setS={setS} state={s}
              opts={[{v:"push_to_talk", l:"Push-to-talk (hold Space)"},
                        {v:"continuous", l:"Continuous (wake-word)"},
                        {v:"meeting",    l:"Meeting record"}]} />
        <Row label="Language" k="language" v={s.language} setS={setS} state={s}
              opts={[{v:"en", l:"English"},
                        {v:"hi", l:"हिन्दी (Hindi)"},
                        {v:"mr", l:"मराठी (Marathi)"},
                        {v:"ta", l:"தமிழ் (Tamil)"},
                        {v:"te", l:"తెలుగు (Telugu)"},
                        {v:"bn", l:"বাংলা (Bengali)"},
                        {v:"gu", l:"ગુજરાતી (Gujarati)"},
                        {v:"pa", l:"ਪੰਜਾਬੀ (Punjabi)"},
                        {v:"kn", l:"ಕನ್ನಡ (Kannada)"},
                        {v:"ml", l:"മലയാളം (Malayalam)"}]} />
        <Toggle label="Speak responses (text-to-speech)" k="feedback"
                v={s.feedback} setS={setS} state={s} />
        <Toggle label="Store transcripts (helps improve accuracy)"
                k="store_transcripts" v={s.store_transcripts} setS={setS} state={s} />
        {s.mode === "continuous" && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, color: "var(--pf-muted)" }}>Wake word</div>
            <input data-testid="voice-wake-word" value={s.wake_word}
                onChange={(e) => setS({ ...s, wake_word: e.target.value })}
                style={{ width: "100%", padding: 6, marginTop: 4, fontSize: 12,
                            background: "var(--pf-bg)", border: "1px solid var(--pf-border)",
                            color: "var(--pf-text)" }} />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end",
                            gap: 8, marginTop: 18 }}>
          <button data-testid="voice-settings-cancel"
              onClick={onClose} style={btnGhost}>Cancel</button>
          <button data-testid="voice-settings-save"
              onClick={save} disabled={saving} style={btn}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <div style={{ marginTop: 12, fontSize: 9, color: "var(--pf-muted)" }}>
          Audio is processed server-side and deleted immediately after
          transcription. Transcripts are stored only with your opt-in.
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, k, v, setS, state, opts }) => (
  <div style={{ marginTop: 10 }}>
    <div style={{ fontSize: 10, color: "var(--pf-muted)" }}>{label}</div>
    <select data-testid={`voice-setting-${k}`} value={v}
        onChange={(e) => setS({ ...state, [k]: e.target.value })}
        style={{ width: "100%", padding: 6, marginTop: 4, fontSize: 12,
                    background: "var(--pf-bg)", border: "1px solid var(--pf-border)",
                    color: "var(--pf-text)" }}>
      {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const Toggle = ({ label, k, v, setS, state }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 8,
                          marginTop: 12, fontSize: 12, cursor: "pointer" }}>
    <input data-testid={`voice-toggle-${k}`} type="checkbox" checked={v}
        onChange={(e) => setS({ ...state, [k]: e.target.checked })}
        style={{ accentColor: "#F26B1F" }} />
    {label}
  </label>
);

const btn = { background: "#F26B1F", color: "white", border: "none",
                  padding: "6px 16px", borderRadius: 4, fontSize: 12,
                  cursor: "pointer" };
const btnGhost = { ...btn, background: "transparent",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)" };
